<template>
    <section class="employee-timesheets" aria-label="Табели сотрудника">
        <header class="employee-timesheets__header">
            <h2><button type="button" class="employee-timesheets__toggle" :aria-expanded="expanded ? 'true' : 'false'" @click="toggle">
                <i class="far fa-calendar-alt" aria-hidden="true"></i> Табели
                <i :class="expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
            </button></h2>
        </header>
        <div v-if="expanded">
            <div class="employee-timesheets__tools">
                <MonthNavigator v-model="month" label="Месяц табелей сотрудника" />
                <button ref="previewButton" type="button" class="btn btn-sm btn-outline-primary" :disabled="!preview || previewLoading || !!previewError" @click="openHours">Часы по дням</button>
                <button type="button" class="employee-timesheets__refresh" :disabled="previewLoading || !month" aria-label="Обновить табели" title="Обновить табели" @click="reload"><i class="fas fa-sync-alt" aria-hidden="true"></i></button>
            </div>
            <div v-if="previewLoading" class="employee-timesheets__state" role="status"><b-spinner small /> Загружаем месяц…</div>
            <div v-if="previewError" class="employee-timesheets__error" role="alert">
                {{ previewError }} <button type="button" class="btn btn-sm btn-outline-danger" @click="loadPreview">Повторить</button>
            </div>
            <p v-if="!month" class="employee-timesheets__state">Выберите месяц.</p>
            <div v-if="preview && !previewLoading && !previewError" class="employee-timesheets__list">
                <article v-for="item in preview.items" :key="item.id" class="employee-timesheets__item">
                    <div><strong>{{ item.object.name }}</strong><small>{{ item.name || `Табель №${item.id}` }} · {{ statusLabel(item.status) }}{{ item.record_kind === 'pilot' ? ' · Пилотный' : '' }}{{ item.historical ? ' · Архив' : '' }}</small></div>
                    <strong>{{ hoursLabel(item.worked_minutes) }}</strong>
                    <router-link :to="timesheetRoute(item.id, preview.employee_id)" target="_blank" rel="noopener noreferrer">Открыть ↗</router-link>
                </article>
                <p v-if="!preview.items.length" class="employee-timesheets__state">За этот месяц доступных табелей нет.</p>
            </div>
            <PayrollTimesheetPopover v-if="month && preview && hoursVisible" ref="hours" :key="contextKey" :month="month" :employee-preview="preview"
                :employee-anchor="previewAnchor" :employee-loading="previewLoading" :employee-error="previewError"
                timesheet-only @month-change="month = $event" @retry="loadPreview" @closed="hoursVisible = false" />
        </div>
    </section>
</template>

<script>
import api from '@/utils/api';
import MonthNavigator from '@/components/MonthNavigator.vue';
import {hoursLabel, statusLabel, timesheetRoute} from '@/utils/employeeTimesheets';
const currentMonth = () => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
};
export default {
    name: 'EmployeeTimesheets',
    components: {MonthNavigator, PayrollTimesheetPopover: () => import('../Payroll/PayrollTimesheetPopover.vue')},
    props: {employeeId: {type: [Number, String], required: true}, contextParams: {type: Object, default: () => ({})}},
    data: () => ({expanded: false, hoursVisible: false, previewAnchor: null, month: currentMonth(), preview: null, previewLoading: false, previewError: '', previewRequestId: 0}),
    computed: {
        contextKey() {
            const user = this.$store.getters.currentUser || {};
            return [user.company, user.userId, this.employeeId, this.contextParams.date, this.contextParams.timesheet_id].join(':');
        }
    },
    watch: {
        contextKey() { this.expanded = false; this.clearPreview(); this.month = currentMonth(); },
        month() { return this.reload(); }
    },
    beforeDestroy() { this.previewRequestId++; },
    methods: {
        hoursLabel, statusLabel, timesheetRoute,
        openHours() { this.previewAnchor = this.$refs.previewButton; this.hoursVisible = true; },
        toggle() {
            this.expanded = !this.expanded;
            if (!this.expanded) { this.hoursVisible = false; return; }
            if (this.preview && this.preview.rows.length) this.openHours();
            if (this.expanded && !this.preview && !this.previewLoading) return this.loadPreview();
        },
        clearPreview() { this.hoursVisible = false; this.previewRequestId++; this.preview = null; this.previewLoading = false; this.previewError = ''; },
        reload() { if (this.expanded) return this.loadPreview(); this.clearPreview(); },
        message(error) { const data = error.response && error.response.data; return data && (data.msg || data.message) || 'Не удалось загрузить табели. Повторите попытку.'; },
        async loadPreview() {
            if (!this.expanded) return;
            const request = ++this.previewRequestId;
            const autoOpen = !this.hoursVisible;
            this.previewError = ''; this.previewLoading = Boolean(this.month);
            if (!this.month) { this.clearPreview(); return; }
            try {
                const {data} = await api.get('/api/employees/' + this.employeeId + '/timesheets/month', {ignoreModal: true,
                    params: {...this.contextParams, month: this.month}});
                if (request === this.previewRequestId) {
                    this.preview = data;
                    await this.$nextTick();
                    if (request === this.previewRequestId && this.expanded && autoOpen && data.rows.length) this.openHours();
                }
            } catch (error) {
                if (request === this.previewRequestId) this.previewError = this.message(error);
            } finally { if (request === this.previewRequestId) this.previewLoading = false; }
        }
    }
};
</script>

<style scoped>
.employee-timesheets { margin: 0; padding: 15px; min-width: 0; color: #34495d; border: 1px solid #dbe3eb; border-radius: 11px; background: #fff; }
.employee-timesheets h2 { margin: 0; font-size: .98rem; font-weight: 700; }
.employee-timesheets__toggle { display: flex; align-items: center; gap: 5px; width: 100%; min-height: 30px; padding: 0; border: 0; background: none; color: inherit; font: inherit; text-align: left; }
.employee-timesheets__toggle > i:first-child { width: 19px; margin-right: 5px; color: #6688a8; text-align: center; }
.employee-timesheets__toggle > i:last-child { margin-left: auto; color: #738293; font-size: 11px; }
.employee-timesheets__tools { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 12px 0; font-size: 12px; }
.employee-timesheets__refresh { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #dbe3eb; border-radius: 5px; background: #fff; color: #738293; }
.employee-timesheets__state { display: flex; align-items: center; gap: 8px; padding: 8px 0; margin: 0; font-size: 13px; color: #738293; }
.employee-timesheets__error { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; color: #972d38; background: #fff1f2; padding: 10px; margin-top: 10px; border-radius: 6px; }
.employee-timesheets__list { max-height: 320px; overflow: auto; }
.employee-timesheets__item { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: 10px; padding: 10px 0; border-top: 1px solid #e1e8ef; font-size: 12px; }
.employee-timesheets__item > div { min-width: 0; overflow-wrap: anywhere; }
.employee-timesheets__item small { display: block; margin-top: 3px; color: #738293; }
.employee-timesheets__item > strong, .employee-timesheets__item > a { white-space: nowrap; }
@media (max-width: 480px) { .employee-timesheets__item { grid-template-columns: minmax(0, 1fr) auto; } .employee-timesheets__item > a { grid-column: 2; } }
</style>
